import { useState, useRef, useEffect } from "react";
import { ModalShell } from "./ModalShell";
import { uploadFile } from "../api/storage";
import { updateOrganisationProfile } from "../api/organisations";
import { updateInstitutionProfile } from "../api/institutions";
import { useAuth } from "../hooks/useAuth";
import { isDisplayableUrl } from "../utils/imageUrl";

export function OrganisationProfileModal({
  isOpen,
  onClose,
  initialData,
  isInstitution = false,
  onProfileUpdated,
}) {
  const { refreshUser } = useAuth();
  const fileInputRef = useRef(null);

  const [name, setName] = useState(initialData?.name || "");
  const [logoUrl, setLogoUrl] = useState(
    isDisplayableUrl(initialData?.logoDownloadUrl)
      ? initialData.logoDownloadUrl
      : "",
  );
  // Track the storage key or raw url that will be saved in DB
  const [logoS3Key, setLogoS3Key] = useState(initialData?.logoUrl || "");
  const [description, setDescription] = useState(
    initialData?.description || "",
  );
  const [websiteUrl, setWebsiteUrl] = useState(initialData?.websiteUrl || "");
  const [contactPhone, setContactPhone] = useState(
    initialData?.contactPhone || "",
  );
  const [addressLine1, setAddressLine1] = useState(
    initialData?.addressLine1 || "",
  );
  const [addressLine2, setAddressLine2] = useState(
    initialData?.addressLine2 || "",
  );
  const [city, setCity] = useState(initialData?.city || "");
  const [state, setState] = useState(initialData?.state || "");
  const [pincode, setPincode] = useState(initialData?.pincode || "");

  useEffect(() => {
    setName(initialData?.name || "");
    setLogoUrl(
      isDisplayableUrl(initialData?.logoDownloadUrl)
        ? initialData.logoDownloadUrl
        : "",
    );
    setLogoS3Key(initialData?.logoUrl || "");
    setDescription(initialData?.description || "");
    setWebsiteUrl(initialData?.websiteUrl || "");
    setContactPhone(initialData?.contactPhone || "");
    setAddressLine1(initialData?.addressLine1 || "");
    setAddressLine2(initialData?.addressLine2 || "");
    setCity(initialData?.city || "");
    setState(initialData?.state || "");
    setPincode(initialData?.pincode || "");
  }, [initialData]);

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  if (!isOpen) return null;

  const entityTypeLabel = isInstitution ? "Institution" : "Organization";

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccessMsg(null);

    // Client-side validation: PNG or JPEG only, max 5MB
    const ALLOWED_TYPES = ["image/jpeg", "image/png"];
    const ext = file.name
      ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
      : "";
    if (
      !ALLOWED_TYPES.includes(file.type) ||
      ![".png", ".jpg", ".jpeg"].includes(ext)
    ) {
      setError("Only PNG and JPEG images are accepted for logos.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File is too large. Maximum size is 5 MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploadingLogo(true);

    try {
      // Local preview immediately for instant feedback
      const previewUrl = URL.createObjectURL(file);
      setLogoUrl(previewUrl);

      // Upload to storage with category "logo"
      const { s3Key } = await uploadFile(file, "logo");
      setLogoS3Key(s3Key);
    } catch (err) {
      setError(err.message || "Failed to upload logo image.");
      setLogoUrl(
        isDisplayableUrl(initialData?.logoDownloadUrl)
          ? initialData.logoDownloadUrl
          : "",
      );
      setLogoS3Key(initialData?.logoUrl || "");
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl("");
    setLogoS3Key("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setSaving(true);

    try {
      const payload = {
        name: name ? name.trim() : undefined,
        logoUrl: logoS3Key ? logoS3Key.trim() : null,
        description: description ? description.trim() : null,
        websiteUrl: websiteUrl ? websiteUrl.trim() : null,
        contactPhone: contactPhone ? contactPhone.trim() : null,
        addressLine1: addressLine1 ? addressLine1.trim() : null,
        addressLine2: addressLine2 ? addressLine2.trim() : null,
        city: city ? city.trim() : null,
        state: state ? state.trim() : null,
        pincode: pincode ? pincode.trim() : null,
      };

      const updateFn = isInstitution
        ? updateInstitutionProfile
        : updateOrganisationProfile;

      const updated = await updateFn(payload);

      // Refresh auth cache so AppShell and other components update immediately
      if (refreshUser) {
        await refreshUser();
      }

      setSuccessMsg(`${entityTypeLabel} profile updated successfully!`);
      if (onProfileUpdated) {
        onProfileUpdated(updated);
      }

      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err) {
      setError(err.message || "Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const currentDisplayName = name || initialData?.name || entityTypeLabel;
  const initialLetters = currentDisplayName
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <ModalShell
      title={`Edit ${entityTypeLabel} Profile`}
      subtitle={`Update your ${entityTypeLabel.toLowerCase()} identity, logo, address, and profile description.`}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col flex-1 p-5 sm:p-6 space-y-5 overflow-y-auto max-h-[75vh]"
      >
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs sm:text-sm rounded-lg flex items-center gap-2">
            <svg
              className="w-4 h-4 shrink-0 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs sm:text-sm rounded-lg flex items-center gap-2">
            <svg
              className="w-4 h-4 shrink-0 text-emerald-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>{successMsg}</span>
          </div>
        )}

        {/* Name input */}
        <div className="space-y-1.5">
          <label
            htmlFor="org-name-input"
            className="text-xs font-bold text-slate-700 tracking-wide uppercase"
          >
            {entityTypeLabel} Name <span className="text-red-500">*</span>
          </label>
          <input
            id="org-name-input"
            type="text"
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              isInstitution
                ? "e.g. National Institute of Technology"
                : "e.g. TechCorp Solutions Private Limited"
            }
            className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
          />
        </div>

        {/* Logo upload section */}
        <div className="flex flex-col sm:flex-row items-center gap-5 p-4 rounded-xl border border-slate-200 bg-slate-50/70">
          <div className="relative group shrink-0">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white border-2 border-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
              {isDisplayableUrl(logoUrl) ? (
                <img
                  src={logoUrl}
                  alt={currentDisplayName}
                  onError={() => setLogoUrl("")}
                  className="w-full h-full object-contain p-1.5"
                />
              ) : (
                <span className="text-xl sm:text-2xl font-extrabold text-[#16294C] tracking-tight">
                  {initialLetters || "TP"}
                </span>
              )}
            </div>
            {uploadingLogo && (
              <div className="absolute inset-0 bg-slate-900/60 rounded-2xl flex items-center justify-center text-white">
                <svg
                  className="animate-spin h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
              </div>
            )}
          </div>

          <div className="flex-1 text-center sm:text-left space-y-2">
            <div>
              <h4 className="text-sm font-bold text-slate-800">
                {entityTypeLabel} Logo
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                PNG or JPEG. Square recommended (max 5 MB).
              </p>
            </div>

            <div className="flex items-center justify-center sm:justify-start gap-2 pt-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleFileChange}
                className="hidden"
                id="org-logo-file-input"
              />
              <button
                type="button"
                disabled={uploadingLogo || saving}
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-300 hover:border-slate-400 text-slate-700 text-xs font-semibold rounded-lg shadow-xs transition-colors cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
              >
                <svg
                  className="w-3.5 h-3.5 text-slate-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                {logoUrl ? "Change Logo" : "Upload Logo"}
              </button>

              {logoUrl && (
                <button
                  type="button"
                  disabled={uploadingLogo || saving}
                  onClick={handleRemoveLogo}
                  className="px-3 py-1.5 text-red-600 hover:bg-red-50 text-xs font-semibold rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Short, One-Line Description / Title */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label
              htmlFor="org-description-input"
              className="text-xs font-bold text-slate-700 tracking-wide uppercase"
            >
              One-Line Title / Description
            </label>
            <span
              className={`text-xs ${
                description.length > 300
                  ? "text-red-500 font-bold"
                  : description.length > 270
                    ? "text-amber-600 font-semibold"
                    : "text-slate-400"
              }`}
            >
              {description.length} / 300
            </span>
          </div>
          <input
            id="org-description-input"
            type="text"
            maxLength={300}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              isInstitution
                ? "e.g. Autonomous engineering institution committed to academic excellence"
                : "e.g. Leading provider of enterprise cloud infrastructure and cybersecurity solutions"
            }
            className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
          />
          <p className="text-xs text-slate-500">
            A concise, one-line summary displayed alongside your brand on job
            opportunities, candidate requests, and credentials.
          </p>
        </div>

        {/* Website URL & Contact Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label
              htmlFor="org-website-input"
              className="text-xs font-bold text-slate-700 tracking-wide uppercase"
            >
              Website URL
            </label>
            <input
              id="org-website-input"
              type="url"
              maxLength={255}
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="org-phone-input"
              className="text-xs font-bold text-slate-700 tracking-wide uppercase"
            >
              Contact Phone
            </label>
            <input
              id="org-phone-input"
              type="tel"
              maxLength={30}
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="e.g. +91 9876543210"
              className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
            />
          </div>
        </div>

        {/* Location & Address Section */}
        <div className="pt-3 border-t border-slate-200/90 space-y-3.5">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-slate-800 tracking-wide uppercase">
              Location & Address
            </h4>
            <span className="text-[11px] text-slate-400 font-medium">
              Official registered address
            </span>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="org-address1-input"
              className="text-xs font-semibold text-slate-600"
            >
              Address Line 1
            </label>
            <input
              id="org-address1-input"
              type="text"
              maxLength={255}
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="e.g. 100 Innovation Park, Tech Boulevard"
              className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="org-address2-input"
              className="text-xs font-semibold text-slate-600"
            >
              Address Line 2 (Optional)
            </label>
            <input
              id="org-address2-input"
              type="text"
              maxLength={255}
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="e.g. Suite 500, Block B"
              className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label
                htmlFor="org-city-input"
                className="text-xs font-semibold text-slate-600"
              >
                City
              </label>
              <input
                id="org-city-input"
                type="text"
                maxLength={100}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Bengaluru"
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="org-state-input"
                className="text-xs font-semibold text-slate-600"
              >
                State
              </label>
              <input
                id="org-state-input"
                type="text"
                maxLength={100}
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="e.g. Karnataka"
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="org-pincode-input"
                className="text-xs font-semibold text-slate-600"
              >
                Pincode
              </label>
              <input
                id="org-pincode-input"
                type="text"
                maxLength={6}
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 560001"
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition-all bg-white"
              />
            </div>
          </div>
        </div>

        {/* Actions footer */}
        <div className="pt-4 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || uploadingLogo}
            className="px-5 py-2 rounded-xl text-xs sm:text-sm font-semibold text-white bg-[#08833E] hover:bg-[#066A32] shadow-sm hover:shadow transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
